import {
  AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, HostListener,
  Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Selector sin ng-select ni estilos externos; compatible con ngModel y Reactive Forms. */
@Component({
  selector: 'app-iguau-select',
  templateUrl: './iguau-select.component.html',
  styleUrls: ['./iguau-select.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => IguauSelectComponent),
    multi: true
  }]
})
export class IguauSelectComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() items: any[] = [];
  @Input() bindLabel = 'label';
  @Input() bindValue = 'value';
  @Input() groupBy = '';
  @Input() placeholder = 'Selecciona una opción';
  @Input() searchPlaceholder = 'Buscar...';
  @Input() notFoundText = 'No se encontraron resultados';
  @Input() clearable = true;
  @Input() searchable = true;
  @Input() disabled = false;
  @Input() id = '';
  @Output() selectionChange = new EventEmitter<any>();
  @ViewChild('trigger') trigger!: ElementRef<HTMLButtonElement>;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  value: any = null;
  open = false;
  term = '';
  activeIndex = -1;
  panelPosition: { top: number; left: number; width: number; maxHeight: number } = {
    top: 0, left: 0, width: 220, maxHeight: 260
  };
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private static counter = 0;
  private readonly uid = 'ig-select-' + (++IguauSelectComponent.counter);

  get panelId(): string { return this.uid + '-panel'; }
  get selectedItem(): any | null {
    return this.items.find(item => this.equal(this.itemValue(item), this.value)) ?? null;
  }
  get selectedLabel(): string {
    return this.selectedItem ? this.itemLabel(this.selectedItem) : '';
  }
  get filtered(): any[] {
    const q = this.normalize(this.term);
    return (this.items ?? []).filter(item =>
      !q || this.normalize(this.itemLabel(item)).includes(q) ||
      (this.groupBy && this.normalize(this.property(item, this.groupBy)).includes(q))
    );
  }
  get groups(): { name: string; items: any[] }[] {
    if (!this.groupBy) return [{ name: '', items: this.filtered }];
    const groups: { name: string; items: any[] }[] = [];
    for (const item of this.filtered) {
      const name = String(this.property(item, this.groupBy) || 'Otros');
      let group = groups.find(g => g.name === name);
      if (!group) { group = { name, items: [] }; groups.push(group); }
      group.items.push(item);
    }
    return groups;
  }

  itemLabel(item: any): string { return String(this.property(item, this.bindLabel) ?? ''); }
  itemValue(item: any): any { return this.property(item, this.bindValue); }
  private property(item: any, key: string): any { return item != null && typeof item === 'object' ? item[key] : item; }
  private equal(a: any, b: any): boolean { return a === b || (a != null && b != null && String(a) === String(b)); }
  private normalize(value: any): string { return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim(); }

  writeValue(value: any): void { this.value = value; }
  registerOnChange(fn: (value: any) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; if (isDisabled) this.close(); }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && this.disabled) this.close();
    if (changes['items'] && this.open) this.activeIndex = this.filtered.length ? 0 : -1;
  }
  ngOnDestroy(): void { this.close(); }

  toggle(): void { this.open ? this.close() : this.show(); }
  show(): void {
    if (this.disabled) return;
    this.term = '';
    this.open = true;
    this.activeIndex = Math.max(0, this.filtered.findIndex(i => this.equal(this.itemValue(i), this.value)));
    if (!this.filtered.length) this.activeIndex = -1;
    this.updatePosition();
    setTimeout(() => { if (this.open && this.searchable) this.searchInput?.nativeElement.focus(); }, 0);
  }
  close(): void {
    if (!this.open) return;
    this.open = false;
    this.onTouched();
  }
  choose(item: any): void {
    const value = this.itemValue(item);
    this.value = value;
    this.onChange(value);
    this.selectionChange.emit(value);
    this.close();
    this.trigger?.nativeElement.focus();
  }
  clear(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) return;
    this.value = null;
    this.onChange(null);
    this.onTouched();
    this.selectionChange.emit(null);
    this.close();
  }
  searchChanged(value: string): void {
    this.term = value;
    this.activeIndex = this.filtered.length ? 0 : -1;
    this.updatePosition();
  }
  /** Selecciona con ratón antes de que el navegador cambie el foco o Bootstrap lo gestione. */
  optionMouseDown(event: MouseEvent, item: any): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.choose(item);
  }
  /** Soporte también para pantallas táctiles y activación accesible con clic. */
  optionClick(event: MouseEvent, item: any): void {
    event.stopPropagation();
    if (this.open) this.choose(item);
  }
  onKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;
    if (event.key === 'Escape') {
      if (this.open) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        this.trigger?.nativeElement.focus();
      }
      return;
    }
    if (event.key === 'Tab') { this.close(); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (!this.open) { this.show(); return; }
      const length = this.filtered.length;
      if (length) {
        this.activeIndex = this.activeIndex < 0 ? 0 :
          (this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + length) % length;
        this.scrollActiveIntoView();
      }
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (!this.open) { this.show(); return; }
      const item = this.filtered[this.activeIndex];
      if (item !== undefined) this.choose(item);
      return;
    }
    if (event.key === ' ' && !this.open && event.target === this.trigger?.nativeElement) {
      event.preventDefault();
      this.show();
    }
  }
  private scrollActiveIntoView(): void {
    // Las opciones visuales y el índice activo siguen el mismo orden que filtered.
    setTimeout(() => {
      if (!this.open) return;
      const options = this.host.nativeElement.querySelectorAll<HTMLElement>('.ig-option');
      options.item(this.activeIndex)?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }
  isActive(item: any): boolean { return this.equal(this.itemValue(item), this.value); }
  isHighlighted(item: any): boolean { return this.filtered[this.activeIndex] === item; }
  @HostListener('document:pointerdown', ['$event'])
  documentClick(event: PointerEvent): void {
    const target = event.target as Node;
    // El panel y el botón están dentro del host del componente.
    if (this.open && !this.host.nativeElement.contains(target)) this.close();
  }
  @HostListener('window:resize')
  resize(): void { if (this.open) this.updatePosition(); }
  @HostListener('document:scroll')
  scroll(): void { if (this.open) this.updatePosition(); }

  constructor(private host: ElementRef<HTMLElement>) {}

  updatePosition(): void {
    if (!this.trigger?.nativeElement) return;
    const rect = this.trigger.nativeElement.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const below = viewportH - rect.bottom - 12;
    const above = rect.top - 12;
    const toTop = below < 220 && above > below;
    const available = Math.max(100, (toTop ? above : below) - 4);
    const maxHeight = Math.min(290, available);
    this.panelPosition = {
      top: toTop ? rect.top - maxHeight - 6 : rect.bottom + 6,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
      width: rect.width,
      maxHeight
    };
  }
}
